import axios from "axios"
import htmx from "htmx.org"
import md5 from "md5"
import {
  onInputChange,
  init,
} from "./fileUploader"
import {
  resizeImage as resizeImageUtil,
} from "./image-resizer"
import { getFileType } from "../utils"

declare global {
  interface Window {
    globalSettingsAssetsFileUploader: {
      init: () => void
      onInputChange: (fileInput: HTMLInputElement, dropzone: HTMLElement) => void
      confirmUpload: () => Promise<void>
    }
  }
}

const RESIZED_IMAGE_MAX_WIDTH = 6000
const ATTACHMENTS_MAX_SIZE_PX = 8000

let progressInterval: number

const checkImageFileSize = async(file: File): Promise<boolean> => {
  return new Promise((resolve, reject) => {
    const img = new Image()
    
    img.onload = () => {
      const width = img.width
      const height = img.height

      if (width > ATTACHMENTS_MAX_SIZE_PX || height > ATTACHMENTS_MAX_SIZE_PX) {
        resolve(false)
      } else {
        resolve(true)
      }        
      
      URL.revokeObjectURL(img.src)
    }

    img.onerror = () => {
      reject(false)
    }

    img.src = URL.createObjectURL(file)
  })
}

const resizeImage = async (file: File): Promise<File> => {
  const resizedData = await resizeImageUtil({ imageFile: file, dimensions: [], customWidth: RESIZED_IMAGE_MAX_WIDTH })
  if (resizedData.success) {
    const resizedImage = resizedData.results.custom
    
    if (resizedImage) {
      return resizedImage
    }
  }
  return file
}

const updateProgressBar = (dropzone: HTMLElement, percentCompleted: number) => {
  const progressBar = dropzone.querySelector("[data-loading-bar]>div") as HTMLDivElement | null

  if (progressBar) {
    progressBar.style.width = `${percentCompleted}%`
  }
}

const uploadSingleFile = async (
  fileInput: HTMLInputElement,
  endpoint: string,
  dropzone: HTMLElement,
  fileIndex: number,
  totalFiles: number,
): Promise<{ key: string, type: string }> => {
  const file = fileInput.files?.[0]
  if (!file) {
    throw new Error("File not found")
  }

  const now = new Date()
  const timestamp = now.toISOString()
  const key = md5(`${file.name}-${timestamp}`)
  const fileType = await getFileType(file) ?? file.type

  const response = await axios({
    method: "POST",
    url: endpoint,
    headers: { "Content-Type": "application/json" },
    data: { key },
  })

  if (response.status !== 200) {
    window.toast.error("Upload failed")
    document.body.dispatchEvent(new CustomEvent("closeModal", { detail: { value: "upload-assets-modal" } }))
    throw new Error(JSON.stringify({ type: "UPLOAD_FAILED", status: response.status, data: response.data }))
  }

  let fileToUpload = file
  if (file.type.startsWith("image/")) {
    try {
      const isValidSize = await checkImageFileSize(file)
      if (!isValidSize) {
        fileToUpload = await resizeImage(file)
      }
    } catch {
      throw new Error("Failed to process image")
    }
  }

  const res = await axios({
    method: "PUT",
    url: response.data.url,
    headers: { "Content-Type": fileToUpload.type },
    data: fileToUpload,
    onUploadProgress: (progressEvent) => {
      if (progressEvent.total) {
        const fileProgress = Math.round((progressEvent.loaded * 35) / progressEvent.total)
        const overallProgress = Math.round((fileIndex * 35 + fileProgress) / totalFiles)
        updateProgressBar(dropzone as HTMLElement, Math.min(overallProgress, 35))
      }
    },
  })

  if (res.status !== 200) {
    window.toast.error("Upload failed")
    document.body.dispatchEvent(new CustomEvent("closeModal", { detail: { value: "upload-assets-modal" } }))
    throw new Error(JSON.stringify({ type: "UPLOAD_FAILED_PUT", status: res.status, data: res.data }))
  }

  return { key, type: fileType! }
}

const assetsConfirmUpload = async() => {
  const buttonSection = document.querySelector("[data-uploaded-files-button]")
  if (!buttonSection) {
    throw new Error("Button not found")
  }
  const button = buttonSection.querySelector("button")
  if (!button) {
    throw new Error("Button not found")
  }
  const dropzone = button.closest(".modal")?.querySelector(".dropzone") as HTMLDivElement | undefined
  if (!dropzone) {
    throw new Error("Dropzone not found")
  }
  const closeModalButton = dropzone.closest(".modal")?.querySelector(".modal__close") as HTMLButtonElement | undefined
  if (!closeModalButton) {
    throw new Error("Close modal button not found")
  }

  button.classList.add("btn--loading")
  closeModalButton.setAttribute("disabled", "true")

  const hiddenInputsSection = document.querySelector("[data-uploaded-files-hidden-inputs]")
  if (!hiddenInputsSection) {
    throw new Error("Hidden inputs section not found")
  }

  const filesInput = Array.from(hiddenInputsSection.querySelectorAll("input"))

  dropzone.classList.add("loading")

  if (filesInput.length === 0 || !filesInput[0].files || filesInput[0].files.length === 0) {
    dropzone.classList.remove("loading")
    button.classList.remove("btn--loading")
    closeModalButton.removeAttribute("disabled")
    throw new Error("No file selected")
  }

  const endpoint = buttonSection.getAttribute("data-upload-url")
  if (!endpoint) {
    dropzone.classList.remove("loading")
    throw new Error("Endpoint not found")
  }

  const updateUrl = buttonSection.getAttribute("data-update-url")
  if (!updateUrl) {
    throw new Error("Update URL not found")
  }

  const updateUrlBody = buttonSection.getAttribute("data-update-url-body") ?? "{}"
  const documentName = (document.querySelector('input[name="documentName"]') as HTMLInputElement)?.value || undefined
  const description = (document.querySelector('textarea[name="uploadImageModalDescription"]') as HTMLTextAreaElement)?.value || undefined

  const uploadedFiles: { key: string, type: string }[] = []

  try {
    for (let i = 0; i < filesInput.length; i++) {
      const result = await uploadSingleFile(filesInput[i], endpoint, dropzone, i, filesInput.length)
      uploadedFiles.push(result)
    }
  } catch (err) {
    button.classList.remove("btn--loading")
    closeModalButton.removeAttribute("disabled")
    dropzone.classList.remove("loading")
    window.toast.error("Upload failed")
    document.body.dispatchEvent(new CustomEvent("closeModal", { detail: { value: "upload-assets-modal" } }))
    throw err
  }

  let progress = 35
  let firstIteration = true

  progressInterval = setInterval(() => {
    if (firstIteration) {
      firstIteration = false
      return
    }
    const increment = Math.floor(Math.random() * 17) + 1
    progress = Math.min(progress + increment, 95)
    updateProgressBar(dropzone, progress)
  }, 2000) as never

  for (const uploaded of uploadedFiles) {
    await htmx.ajax("post", updateUrl, {
      values: {
        ...JSON.parse(updateUrlBody),
        ...documentName ? { documentName } : {},
        ...description ? { description } : {},
        documentKey: uploaded.key,
        documentType: uploaded.type,
      },
    })
  }

  const submitButton = document.querySelector("[data-uploaded-files-button]")?.querySelector("button")
  if (!submitButton) {
    clearInterval(progressInterval)
    return
  }

  submitButton.classList.remove("btn--loading")
  closeModalButton.removeAttribute("disabled")
  clearInterval(progressInterval)
}

window.globalSettingsAssetsFileUploader = {
  init,
  onInputChange,
  confirmUpload: assetsConfirmUpload
}
